/* ═══════════════════════════════════════════════════════════════
   PNH Cart Premium JS
   - Accordion (coupon code + order notes)
   - Discount code → checkout URL injection
   - Sticky checkout (IntersectionObserver)
   - Recommended products (async fetch + render)
═══════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  /* ── Translated UI strings (injected by the section via a JSON script
        tag); every T() call falls back to the built-in English string. ── */
  var PNH_I18N = {};
  (function () {
    var el = document.querySelector('[data-pnh-cart-i18n]');
    if (el) { try { PNH_I18N = JSON.parse(el.textContent) || {}; } catch (e) { PNH_I18N = {}; } }
  })();
  function T(key, fallback) { return typeof PNH_I18N[key] === 'string' ? PNH_I18N[key] : fallback; }

  /* ── Safe storage helpers (private browsing throws SecurityError) ── */
  function ssGet(key) { try { return sessionStorage.getItem(key); } catch (_) { return null; } }
  function ssSet(key, val) { try { sessionStorage.setItem(key, val); } catch (_) {} }
  function ssRemove(key) { try { sessionStorage.removeItem(key); } catch (_) {} }

  /* ── Accordion ──────────────────────────────────────────────── */
  function initAccordions() {
    document.querySelectorAll('.pnh-accordion__trigger').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var accordion = this.closest('.pnh-accordion');
        var isOpen = accordion.classList.contains('pnh-accordion--open');
        accordion.classList.toggle('pnh-accordion--open', !isOpen);
        this.setAttribute('aria-expanded', String(!isOpen));
      });
    });
  }

  /* ── Discount code ──────────────────────────────────────────── */
  var DISCOUNT_KEY = 'pnh_discount_code';

  /* Bind each coupon form once. The cart drawer replaces its whole innerHTML
     after every AJAX add-to-cart, so forms rendered later must be bound too
     (see observeCartRerenders) — otherwise "Apply" reloads the page. */
  function initCoupon() {
    document.querySelectorAll('.pnh-coupon-form').forEach(function (form) {
      if (form.dataset.pnhBound) return;
      form.dataset.pnhBound = '1';
      initCouponForm(form);
    });
  }

  function initCouponForm(form) {
    var input       = form.querySelector('.pnh-coupon-input');
    var btn         = form.querySelector('.pnh-coupon-btn');
    var message     = form.querySelector('.pnh-coupon-message');
    var inputWrap   = form.querySelector('.pnh-coupon-input-wrap');
    var appliedWrap = form.querySelector('.pnh-coupon-applied');
    var appliedCode = form.querySelector('.pnh-coupon-applied__code');
    var removeBtn   = form.querySelector('.pnh-coupon-applied__remove');
    var submitting  = false;

    /* Restore previously validated code — but verify it's still live in
       the Shopify session before showing the applied state. The cart's
       session discount can be cleared by Shopify (expiry, cart change,
       another /discount/ call in a different tab) without us knowing,
       so trusting sessionStorage alone risks showing "applied" for a
       code that checkout will silently drop. */
    var saved = ssGet(DISCOUNT_KEY);
    if (saved) {
      showAppliedState(saved);
      fetch('/cart.js', {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        cache: 'no-store'
      })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) {
          if (!cart) return;
          var stillApplied = isCodeApplied(cart, saved);
          if (!stillApplied) {
            ssRemove(DISCOUNT_KEY);
            showInputState();
            syncAllForms(null);
          }
        })
        .catch(function () { /* network blip — leave UI as-is */ });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (submitting) return;

      var code = (input ? input.value : '').trim().toUpperCase();
      if (!code) {
        showMsg(T('enterCode', 'Please enter a discount code.'), 'error');
        return;
      }

      submitting = true;
      setLoading(true);
      showMsg('', '');

      /* Step 1 — register the code with the Shopify cart session.
         /discount/CODE accepts the request regardless of whether the
         code exists; validation happens server-side when the cart
         re-renders, so we must re-fetch cart.js to confirm. */
      fetch('/discount/' + encodeURIComponent(code), {
        redirect: 'follow',
        credentials: 'same-origin'
      })
        .then(function () {
          /* Step 2 — cart.discount_codes is the authoritative signal.
             A valid code that applies to the current cart appears with
             applicable: true. A non-existent / expired code either
             never appears OR appears with applicable: false. We treat
             applicable !== true as a rejection so the customer learns
             on the cart page instead of being surprised at checkout. */
          return fetch('/cart.js', {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            credentials: 'same-origin',
            cache: 'no-store'
          });
        })
        .then(function (r) {
          if (!r.ok) throw new Error('Cart fetch failed');
          return r.json();
        })
        .then(function (cart) {
          var codes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
          var match = null;
          for (var i = 0; i < codes.length; i++) {
            if (codes[i] && String(codes[i].code).toUpperCase() === code) {
              match = codes[i];
              break;
            }
          }

          if (match && match.applicable === true) {
            ssSet(DISCOUNT_KEY, code);
            showAppliedState(code);
            syncAllForms(code);
            showMsg(T('applied', 'Discount applied.'), 'success');
          } else if (match && match.applicable === false) {
            /* Code exists but doesn't meet conditions for this cart
               (minimum spend, eligible products, customer-specific,
               not-yet-active, etc.). */
            ssRemove(DISCOUNT_KEY);
            showMsg(T('notValidCart', 'This code is not valid for your current cart.'), 'error');
            if (input) { input.select(); input.focus(); }
          } else {
            ssRemove(DISCOUNT_KEY);
            showMsg(T('invalidCode', 'Invalid coupon code.'), 'error');
            if (input) { input.select(); input.focus(); }
          }
          done();
        })
        .catch(function () {
          showMsg(T('validateFail', 'Unable to validate. Please try again.'), 'error');
          done();
        });
    });

    if (removeBtn) {
      removeBtn.addEventListener('click', function () {
        removeBtn.disabled = true;

        /* Optimistic local clear so the UI responds immediately */
        ssRemove(DISCOUNT_KEY);
        syncAllForms(null);

        /* POST to /cart with discount= clears the server-side session
           discount that /discount/CODE set. Reload afterwards so
           Liquid-rendered totals reflect the true cart state. */
        fetch('/cart', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: 'discount=',
          redirect: 'follow'
        })
        .then(function () { window.location.reload(); })
        .catch(function () { window.location.reload(); });
      });
    }

    function done() {
      submitting = false;
      setLoading(false);
    }

    function setLoading(on) {
      if (btn) { btn.disabled = on; btn.classList.toggle('pnh-coupon-btn--loading', on); }
      if (input) input.disabled = on;
    }

    function showMsg(text, type) {
      if (!message) return;
      message.textContent = text;
      message.className = 'pnh-coupon-message' + (type ? ' pnh-coupon-message--' + type : '');
    }

    function showAppliedState(code) {
      if (appliedCode) appliedCode.textContent = code;
      if (inputWrap)   inputWrap.hidden  = true;
      if (appliedWrap) appliedWrap.hidden = false;
    }

    function showInputState() {
      if (inputWrap)   inputWrap.hidden  = false;
      if (appliedWrap) appliedWrap.hidden = true;
    }
  }

  /* True only when the code appears in cart.discount_codes with
     applicable:true — same strict rule used at submit time. */
  function isCodeApplied(cart, code) {
    if (!cart || !code) return false;
    var codes = Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
    var upper = String(code).toUpperCase();
    for (var i = 0; i < codes.length; i++) {
      if (codes[i] && String(codes[i].code).toUpperCase() === upper) {
        return codes[i].applicable === true;
      }
    }
    return false;
  }

  /* Sync all coupon form instances on the page (cart page + drawer) */
  function syncAllForms(code) {
    document.querySelectorAll('.pnh-coupon-form').forEach(function (f) {
      var iw  = f.querySelector('.pnh-coupon-input-wrap');
      var aw  = f.querySelector('.pnh-coupon-applied');
      var ac  = f.querySelector('.pnh-coupon-applied__code');
      var inp = f.querySelector('.pnh-coupon-input');
      var msg = f.querySelector('.pnh-coupon-message');
      if (code) {
        if (ac)  ac.textContent  = code;
        if (iw)  iw.hidden       = true;
        if (aw)  aw.hidden       = false;
      } else {
        if (iw)  iw.hidden       = false;
        if (aw)  aw.hidden       = true;
        if (inp) inp.value       = '';
        if (msg) { msg.textContent = ''; msg.className = 'pnh-coupon-message'; }
      }
    });
  }

  /* ── Inject discount into checkout URL ─────────────────────── */
  function initCheckoutDiscount() {
    document.querySelectorAll('[name="checkout"]').forEach(function (btn) {
      if (btn.dataset.pnhDiscountBound) return;
      btn.dataset.pnhDiscountBound = '1';
      btn.addEventListener('click', function (e) {
        var code = ssGet(DISCOUNT_KEY);
        if (!code) return;
        e.preventDefault();
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        window.location.href = root + 'checkout?discount=' + encodeURIComponent(code);
      });
    });
  }

  /* ── Sticky checkout (mobile) ───────────────────────────────── */
  function initStickyCheckout() {
    var stickyBar   = document.querySelector('.pnh-sticky-checkout');
    var anchorBtn   = document.getElementById('checkout');
    var totalValue  = document.querySelector('.pnh-sticky-checkout__total-value');

    if (!stickyBar || !anchorBtn) return;

    /* Watch anchor button visibility */
    var observer = new IntersectionObserver(function (entries) {
      var visible = entries[0].isIntersecting;
      stickyBar.classList.toggle('is-visible', !visible);
    }, { threshold: 0.1 });

    observer.observe(anchorBtn);

    /* Re-evaluate on resize/rotation so sticky bar state stays correct */
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        observer.unobserve(anchorBtn);
        observer.observe(anchorBtn);
      }, 250);
    });

    /* Sync total price into sticky bar */
    syncStickyTotal(totalValue);

    /* MutationObserver to catch AJAX cart updates */
    var totalEl = document.querySelector('.totals__total-value');
    if (totalEl && totalValue) {
      new MutationObserver(function () {
        syncStickyTotal(totalValue);
      }).observe(totalEl, { childList: true, subtree: true, characterData: true });
    }

    /* Sticky CTA click */
    var stickyBtn = stickyBar.querySelector('.pnh-sticky-checkout__btn');
    if (stickyBtn) {
      stickyBtn.addEventListener('click', function () {
        var code = ssGet(DISCOUNT_KEY);
        if (code) {
          var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
          window.location.href = root + 'checkout?discount=' + encodeURIComponent(code);
        } else {
          anchorBtn.click();
        }
      });
    }
  }

  function syncStickyTotal(el) {
    if (!el) return;
    var source = document.querySelector('.totals__total-value');
    if (source) el.textContent = source.textContent.trim();
  }

  /* ── Recommended products ───────────────────────────────────── */
  var _recsController = null;

  /* Utility products (e.g. the donation) must never surface as a recommendation */
  var EXCLUDED_REC_HANDLES = ['donation'];
  function isRecAllowed(p) {
    if (!p) return false;
    var h = (p.handle || '').toLowerCase();
    if (EXCLUDED_REC_HANDLES.indexOf(h) !== -1) return false;
    if ((p.type || '').toLowerCase() === 'donation') return false;
    return true;
  }

  function initRecommendations() {
    var container = document.querySelector('.pnh-cart-recs');
    if (!container) return;

    var productId = container.dataset.productId;
    var grid      = container.querySelector('.pnh-cart-recs__grid');
    if (!productId || !grid) {
      container.classList.add('pnh-cart-recs--hidden');
      return;
    }

    /* Abort any in-flight request before starting a new one */
    if (_recsController) _recsController.abort();
    _recsController = new AbortController();
    var signal = _recsController.signal;

    /* Show skeleton loaders */
    grid.innerHTML = buildSkeletons(4);

    /* Locale root ("/ar/"…): fetching from "/" returned English titles and English
       product links on every translated storefront (2026-10-13 audit). */
    var recsRoot = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/?$/, '/');
    var url = recsRoot + 'recommendations/products.json?product_id=' + productId + '&limit=4&intent=complementary';

    fetch(url, { signal: signal })
      .then(function (r) { if (!r.ok) throw new Error('Recs HTTP ' + r.status); return r.json(); })
      .then(function (data) {
        var products = (data.products || []).filter(isRecAllowed);
        /* Fall back to related intent if complementary returns nothing (or only excluded items) */
        if (products.length === 0) {
          return fetch(recsRoot + 'recommendations/products.json?product_id=' + productId + '&limit=4', { signal: signal })
            .then(function (r) { if (!r.ok) throw new Error('Recs HTTP ' + r.status); return r.json(); });
        }
        return data;
      })
      .then(function (data) {
        var products = (data.products || []).filter(isRecAllowed);
        if (products.length === 0) {
          container.classList.add('pnh-cart-recs--hidden');
          return;
        }
        grid.innerHTML = products.map(buildRecCard).join('');
        bindAddToCart(grid);
      })
      .catch(function (err) {
        if (err.name !== 'AbortError') container.classList.add('pnh-cart-recs--hidden');
      });
  }

  function buildSkeletons(n) {
    var html = '';
    for (var i = 0; i < n; i++) {
      html += '<div class="pnh-rec-skeleton">'
            +   '<div class="pnh-rec-skeleton__media"></div>'
            +   '<div class="pnh-rec-skeleton__body">'
            +     '<div class="pnh-rec-skeleton__line"></div>'
            +     '<div class="pnh-rec-skeleton__line pnh-rec-skeleton__line--short"></div>'
            +     '<div class="pnh-rec-skeleton__btn"></div>'
            +   '</div>'
            + '</div>';
    }
    return html;
  }

  function getProductImageSrc(p) {
    var raw = '';
    if (p.featured_image) {
      raw = typeof p.featured_image === 'string' ? p.featured_image : (p.featured_image.src || '');
    } else if (p.images && p.images[0]) {
      raw = typeof p.images[0] === 'string' ? p.images[0] : (p.images[0].src || '');
    }
    if (!raw) return '';
    // Only trust Shopify CDN origins (source is /recommendations/products.json)
    var ok = raw.indexOf('https://cdn.shopify.com/') === 0 ||
             raw.indexOf('//cdn.shopify.com/') === 0 ||
             raw.charAt(0) === '/';
    if (!ok) return '';
    return raw + (raw.indexOf('?') !== -1 ? '&' : '?') + 'width=400';
  }

  function buildRecCard(p) {
    var imgSrc = getProductImageSrc(p);
    var imgHtml = imgSrc
      ? '<img src="' + imgSrc + '" alt="' + esc(p.title) + '" loading="lazy" width="400" height="400">'
      : '<div style="background:#e0dbd2;width:100%;height:100%"></div>';

    var price   = formatMoney(p.price_min);
    var varId   = p.variants && p.variants[0] ? p.variants[0].id : '';
    var available = p.variants && p.variants[0] ? p.variants[0].available : false;

    var safeUrl = esc(p.url || '');
    return '<div class="pnh-rec-card">'
         +   '<a href="' + safeUrl + '" class="pnh-rec-card__media" tabindex="-1" aria-hidden="true">' + imgHtml + '</a>'
         +   '<div class="pnh-rec-card__body">'
         +     '<a href="' + safeUrl + '" class="pnh-rec-card__title">' + esc(p.title) + '</a>'
         +     '<p class="pnh-rec-card__price">' + price + '</p>'
         +     '<button class="pnh-rec-card__btn"'
         +       ' data-variant-id="' + varId + '"'
         +       ' aria-label="' + (available ? T('addAria', 'Add __T__ to cart').replace('__T__', esc(p.title)) : T('soldOutAria', '__T__ — Sold out').replace('__T__', esc(p.title))) + '"'
         +       (available ? '' : ' disabled aria-disabled="true"')
         +     '>'
         +       (available ? T('addToCart', 'Add to cart') : T('soldOut', 'Sold out'))
         +     '</button>'
         +   '</div>'
         + '</div>';
  }

  function bindAddToCart(grid) {
    grid.querySelectorAll('.pnh-rec-card__btn:not(:disabled)').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var variantId = this.dataset.variantId;
        var self      = this;

        self.disabled    = true;
        self.textContent = T('adding', 'Adding…');

        fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body:    JSON.stringify({ id: Number(variantId), quantity: 1 })
        })
        .then(function (r) {
          return r.json().then(function (data) {
            if (!r.ok) {
              /* Shopify returns {status, message, description} on errors (e.g. 422) */
              throw new Error((data && (data.description || data.message)) || T('addFail', 'Could not add to cart'));
            }
            return data;
          });
        })
        .then(function () {
          self.textContent = T('added', '✓ Added!');
          self.classList.add('pnh-rec-card__btn--added');

          /* Fire pubsub event so cart header count updates */
          if (window.PubSub && typeof window.PubSub.publish === 'function') {
            fetch('/cart.js')
              .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
              .then(function (cart) {
                window.PubSub.publish('cart:refresh', cart);
              })
              .catch(function () { /* the reload below refreshes the count anyway */ });
          }

          /* Reload after brief success moment so cart updates */
          setTimeout(function () { window.location.reload(); }, 900);
        })
        .catch(function (err) {
          self.disabled    = false;
          self.textContent = T('addToCart', 'Add to cart');
          if (err && err.message) self.title = err.message;
        });
      });
    });
  }

  /* ── Donation (event-delegated so it survives cart-drawer re-renders) ── */
  function initDonation() {
    if (window.__pnhDonationBound) return;
    window.__pnhDonationBound = true;

    document.addEventListener('click', function (e) {
      var addBtn    = e.target.closest('[data-pnh-donation-add]');
      var removeBtn = e.target.closest('[data-pnh-donation-remove]');
      var btn = addBtn || removeBtn;
      if (!btn) return;

      var widget = btn.closest('[data-pnh-donation]');
      var variantId = btn.dataset.variantId;
      if (!widget || !variantId) return;

      var status = widget.querySelector('.pnh-donation__status');
      widget.querySelectorAll('.pnh-donation__btn, .pnh-donation__remove-link').forEach(function (b) { b.disabled = true; });
      btn.dataset.label = btn.textContent;
      btn.textContent = addBtn ? T('adding', 'Adding…') : T('removing', 'Removing…');
      if (status) status.textContent = btn.textContent;

      /* add a tier, or remove the line (quantity 0 by variant id) */
      var url  = addBtn ? '/cart/add.js' : '/cart/change.js';
      var data = addBtn ? { id: Number(variantId), quantity: 1 }
                        : { id: String(variantId), quantity: 0 };

      fetch(url, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body:    JSON.stringify(data)
      })
        .then(function (r) {
          return r.json().then(function (d) {
            if (!r.ok) throw new Error((d && (d.description || d.message)) || T('requestFailed', 'Request failed'));
            return d;
          });
        })
        .then(function () {
          /* Inside the cart drawer: re-render just the drawer (Liquid decides
             totals, widget state and the empty state), no page reload.
             Elsewhere (cart page): reload so the Liquid cart is accurate. */
          var drawer = widget.closest('cart-drawer');
          if (drawer && typeof window.refreshCartDrawer === 'function') {
            return Promise.resolve(window.refreshCartDrawer()).then(function () {
              var inner = drawer.querySelector('.drawer__inner');
              if (inner) inner.focus();
            });
          }
          window.location.reload();
        })
        .catch(function (err) {
          widget.querySelectorAll('.pnh-donation__btn, .pnh-donation__remove-link').forEach(function (b) {
            b.disabled = false;
            if (b.dataset.label) { b.textContent = b.dataset.label; }
          });
          if (status) status.textContent = (err && err.message) ? err.message : T('donationFail', 'Could not update donation.');
        });
    });
  }

  /* ── Utilities ──────────────────────────────────────────────── */
  function esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatMoney(cents) {
    /* Prefer the theme's configured money format (carries the store currency) */
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.theme && window.theme.moneyFormat) {
      return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
    }
    /* Otherwise format in the active store currency — never hardcode '$' */
    var amount = (cents || 0) / 100;
    var code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EGP';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: code }).format(amount);
    } catch (e) {
      return amount.toFixed(2) + ' ' + code;
    }
  }

  /* ── Boot ───────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  function boot() {
    if (window.__pnhCartBooted) return;
    window.__pnhCartBooted = true;
    initAccordions();
    initCoupon();
    initCheckoutDiscount();
    initStickyCheckout();
    initRecommendations();
    initDonation();
    observeCartRerenders();
  }

  /* Re-bind coupon forms and checkout buttons that arrive after boot (the
     cart drawer is re-rendered on every add-to-cart). Each binder is
     idempotent via a data flag, so repeated mutations are harmless. */
  function observeCartRerenders() {
    if (!window.MutationObserver || !document.body) return;
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        initCoupon();
        initCheckoutDiscount();
      });
    }).observe(document.body, { childList: true, subtree: true });
  }
})();
